import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useLocation, Link } from "react-router-dom";
import './NewNote.css';
import { createPost, createPostForPlan } from '../../actions/postAction';
import { decreasetokens } from '../../actions/userAction';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import TokenPopup from './TokenPopup';
import TypeOfNote from './TypeOfNote';

const LOCAL_STORAGE_KEY = 'newNoteDraftContent';
const MAX_CHARS = 1500;

const CATEGORIES = [
  { key: "health",        label: "Health",        icon: "◎" },
  { key: "career",        label: "Career",        icon: "⬡" },
  { key: "business",      label: "Business",      icon: "◈" },
  { key: "finance",       label: "Finance",       icon: "✦" },
  { key: "relationships", label: "Relationships", icon: "⬭" },
  { key: "other",         label: "Other",         icon: "○" },
];

const EXTRA_CATEGORIES = [
  "Mindset", "Spirituality", "Family", "Social",
  "Fitness", "Purpose", "Wealth", "Habits",
  "Environment", "Art", "Writing", "Side-Quest",
];

const PRESET_KEYS = CATEGORIES.map((c) => c.key);

const PLACEHOLDER_TEXT = "What's on your mind?...";

/* ── EXTRA CATEGORY MODAL ── */
const ExtraCategoryModal = ({ existingKey, onSelect, onClose }) => {
  const [selected, setSelected] = useState(null);
  const [customInput, setCustomInput] = useState("");
  const [customAdded, setCustomAdded] = useState([]);

  const allPreset = EXTRA_CATEGORIES;

  const handleAddCustom = () => {
    const val = customInput.trim();
    if (!val) return;
    if (customAdded.includes(val) || allPreset.map(c => c.toLowerCase()).includes(val.toLowerCase())) {
      setCustomInput("");
      return;
    }
    setCustomAdded((prev) => [...prev, val]);
    setSelected(val);
    setCustomInput("");
  };

  const allOptions = [...allPreset, ...customAdded];

  return (
    <div className="ccm-overlay" onClick={onClose}>
      <div className="ccm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccm-header">
          <span className="ccm-title">More Categories</span>
          <button className="ccm-close" onClick={onClose} type="button">✕</button>
        </div>

        <p className="ccm-hint">Pick from the list or type your own.</p>

        <div className="ccm-grid">
          {allOptions.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`ccm-item ${selected === cat ? "selected" : ""}`}
              onClick={() => setSelected(cat)}
            >
              <span className="ccm-label">{cat}</span>
              {selected === cat && <span className="ccm-check">✓</span>}
            </button>
          ))}
        </div>

        <div className="ccm-custom-input-row">
          <input
            type="text"
            className="ccm-custom-input"
            placeholder="e.g. Gaming, Side hustle..."
            value={customInput}
            maxLength={30}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddCustom()}
          />
          <button
            className="ccm-custom-add-btn"
            onClick={handleAddCustom}
            disabled={!customInput.trim()}
            type="button"
          >
            Add
          </button>
        </div>

        <div className="ccm-footer">
          <button className="ccm-cancel" onClick={onClose} type="button">Cancel</button>
          <button
            className="ccm-confirm"
            disabled={!selected}
            onClick={() => { onSelect(selected); onClose(); }}
            type="button"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── MAIN COMPONENT ── */
const NewNote = () => {
  const { user }   = useSelector((state) => state.user);
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { planId } = useParams();

  const [step, setStep]                   = useState(1);
  const [tokenCount, setTokenCount]       = useState(1);
  const [content, setContent]             = useState('');
  const [selectedOption, setSelectedOption] = useState('digicoins');
  const [btnloading, setbtnloading]       = useState(false);
  const [showPopup, setShowPopup]         = useState(false);
  const [showNoti, setshowNoti]           = useState(false);
  const [isSaving, setIsSaving]           = useState(false);
  const [category, setCategory]           = useState("");
  const [showExtraModal, setShowExtraModal] = useState(false);

  // If category from modal is not a preset, we track it as custom
  const [customCategory, setCustomCategory] = useState(null);

  const [showCongrats, setShowCongrats]   = useState(false);
  const [congratsType, setCongratsType]   = useState(null);
  const [targetDays, setTargetDays]       = useState(0);
  const [displayDays, setDisplayDays]     = useState(0);
  const [createdPostId, setCreatedPostId] = useState(null);
  const [createdPlanId, setCreatedPlanId] = useState(null);

  /* Preselect from nav state */
  useEffect(() => {
    if (location.state?.preselectedCategory) {
      const pre = location.state.preselectedCategory;
      const isPreset = PRESET_KEYS.includes(pre.toLowerCase());
      if (isPreset) {
        setCategory(pre.toLowerCase());
      } else {
        setCustomCategory(pre);
        setCategory(pre);
      }
    }
  }, [location.state]);

  /* Load draft */
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) setContent(saved);
  }, []);

  /* Autosave draft */
  useEffect(() => {
    setIsSaving(true);
    const t = setTimeout(() => {
      if (content.trim()) {
        localStorage.setItem(LOCAL_STORAGE_KEY, content);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
      setIsSaving(false);
    }, 1000);
    return () => clearTimeout(t);
  }, [content]);

  useEffect(() => {
    if (user.country && user.country !== "IN") setshowNoti(true);
  }, [user.country]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (content.length > MAX_CHARS) {
      toast.error(`Note exceeds ${MAX_CHARS} characters.`);
      return;
    }
    if (selectedOption === "digicoins" && user.tokens === 0) {
      toast.error("Insufficient D-credits.", { autoClose: false });
      return;
    }
    const data = { note: content, method: selectedOption, category: category || "other" };
    try {
      setbtnloading(true);
      let res;
      if (planId) {
        res = await dispatch(createPostForPlan({ ...data, planId }));
        setCreatedPlanId(planId);
        setCongratsType('plan');
        navigate(`/dashboard/year-plan/${planId}`);
      } else {
        res = await dispatch(createPost(data));
        setCreatedPostId(res._id);
        setCongratsType('note');
        setShowCongrats(true);
      }
      if (res && selectedOption === "digicoins") await dispatch(decreasetokens(1));
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setCategory("");
      setCustomCategory(null);
      setContent("");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setbtnloading(false);
    }
  };

  /* Congrats — plan */
  useEffect(() => {
    if (!showCongrats || congratsType !== 'plan') return;
    if (targetDays === 0) {
      const t = setTimeout(() => {
        setShowCongrats(false);
        navigate(`/dashboard/year-plan/${createdPlanId}`);
      }, 1200);
      return () => clearTimeout(t);
    }
    const steps = Math.max(1, Math.ceil(2000 / 40));
    const increment = Math.max(1, Math.ceil(targetDays / steps));
    let current = 0;
    const iv = setInterval(() => {
      current += increment;
      if (current >= targetDays) {
        setDisplayDays(targetDays);
        clearInterval(iv);
        setTimeout(() => {
          setShowCongrats(false);
          navigate(`/dashboard/year-plan/${createdPlanId}`);
        }, 800);
      } else {
        setDisplayDays(current);
      }
    }, 40);
    return () => clearInterval(iv);
  }, [showCongrats, congratsType, targetDays, createdPlanId, navigate]);

  /* Congrats — note */
  useEffect(() => {
    if (!showCongrats || congratsType !== 'note') return;
    const t = setTimeout(() => {
      setShowCongrats(false);
      if (createdPostId) navigate(`/dashboard/note-details/${createdPostId}`);
    }, 1500);
    return () => clearTimeout(t);
  }, [showCongrats, congratsType, createdPostId, navigate]);

  const isCustomSelected = category && !PRESET_KEYS.includes(category.toLowerCase());
  const isNextDisabled   = content.trim().length === 0 || content.length > MAX_CHARS || (!planId && !category.trim());

  return (
    <div className="newnote-container">
      <Helmet><title>New Record</title></Helmet>

      {/* ── STEP 1: WRITE ── */}
      {step === 1 && (
        <>
          <h2>{planId ? "Add a Record." : "Write a Record."}</h2>
          <p className="newnote-subtitle">
            {planId
              ? "A decision, doubt, milestone, or why — anything that belongs in the journey of this Vision."
              : "A single intention, idea, or reality you want your future self to see. Anonymous until you release it."}
          </p>
          <Link
            to="/blog/how-to-write-your-first-node"
            target="_blank"
            rel="noopener noreferrer"
            className="newnote-howto-link"
          >
            Not sure how to write this? See examples and tips →
          </Link>

          {/* EDITOR — always first */}
          <div className="newnote-editor-card visible">
            <div className="newnote-editor-header">
              {!planId && category && <span className="newnote-editor-pill">{category}</span>}
              <span className={`autosave-status ${isSaving ? "saving" : ""}`}>
                {isSaving ? "Saving draft…" : "Draft saved"}
              </span>
            </div>

            <textarea
              className="newnote-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={PLACEHOLDER_TEXT}
              autoFocus
            />

            <div className="newnote-editor-footer">
              <span className={`char-counter ${content.length >= MAX_CHARS * 0.9 ? "limit-warning" : ""}`}>
                {content.length} / {MAX_CHARS}
              </span>
            </div>
          </div>

          {!planId && (
            <>
              <div className="newnote-divider" />
              <div className="newnote-section">
                <p className="hint-text">What category is this?</p>
                <div className="newnote-cat-grid">
                  {CATEGORIES.map(({ key, label, icon }) => (
                    <button
                      key={key}
                      type="button"
                      className={`newnote-cat-btn ${category === key ? "active" : ""}`}
                      onClick={() => { setCategory(key); setCustomCategory(null); }}
                    >
                      <span className="newnote-cat-icon">{icon}</span>
                      {label}
                    </button>
                  ))}

                  {isCustomSelected && (
                    <button
                      type="button"
                      className="newnote-cat-btn active"
                      onClick={() => setShowExtraModal(true)}
                    >
                      <span className="newnote-cat-icon">◇</span>
                      {category}
                    </button>
                  )}

                  <button
                    type="button"
                    className="newnote-cat-btn newnote-cat-more"
                    onClick={() => setShowExtraModal(true)}
                  >
                    <span className="newnote-cat-icon">+</span>
                    More
                  </button>
                </div>
              </div>
            </>
          )}

          {/* FOOTER */}
          <div className="newnote-footer">
            <button
              className="primary-btn"
              type="button"
              onClick={() => setStep(2)}
              disabled={isNextDisabled}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: TYPE ── */}
      {step === 2 && (
        <div className="plan-step-container">
          <TypeOfNote
            user={user}
            creditsRequired={1}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            handleBack={() => setStep(1)}
            handlePost={handlePost}
            btnloading={btnloading}
            showNoti={showNoti}
            setshowNoti={setshowNoti}
            setShowPopup={setShowPopup}
          />
        </div>
      )}

      {/* ── TOKEN POPUP ── */}
      {showPopup && (
        <TokenPopup
          tokenCount={tokenCount}
          updateTokenCount={setTokenCount}
          closePopup={() => setShowPopup(false)}
        />
      )}

      {/* ── EXTRA CATEGORY MODAL ── */}
      {showExtraModal && (
        <ExtraCategoryModal
          existingKey={category}
          onSelect={(cat) => { setCategory(cat); setCustomCategory(cat); }}
          onClose={() => setShowExtraModal(false)}
        />
      )}

      {/* ── CONGRATS OVERLAY ── */}
      {showCongrats && (
        <div className="congrats-overlay">
          <div className="congrats-modal">
            {congratsType === 'plan' ? (
              <>
                <div className="congrats-badge">Vision Created</div>
                <h2 className="congrats-title">You have a plan.</h2>
                <p className="congrats-sub">Days remaining in this vision</p>
                <div className="congrats-counter">{displayDays}</div>
                <div className="congrats-actions">
                  <button
                    className="congrats-btn primary"
                    onClick={() => { setShowCongrats(false); navigate(`/dashboard/year-plan/${createdPlanId}`); }}
                  >
                    View Vision
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="congrats-badge">Record Posted</div>
                <div className="congrats-emoji">✦</div>
                <h2 className="congrats-title">All the best.</h2>
                <p className="congrats-sub">Your record is live. Future you will see this.</p>
                <div className="congrats-actions">
                  <button
                    className="congrats-btn primary"
                    onClick={() => { setShowCongrats(false); if (createdPostId) navigate(`/dashboard/note-details/${createdPostId}`); }}
                  >
                    View Record
                  </button>
                  <button className="congrats-btn secondary" onClick={() => setShowCongrats(false)}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default NewNote;